<template>
    <div class="focal-selector" :class="orientation">
        <div class="video-left-control-container">
            <div class="video-left-control-item left">{{ $translate('focal') }}</div>
            <div class="video-left-control-item right">{{ focal }}</div>
        </div>

        <div class="focal-selector-slider">
            <div class="ticks-container">
                <span>{{ min }}</span>
                <span>{{ availableFocals[midFocalIndex] }}</span>
                <span>{{ max }}</span>
            </div>

            <b-form-slider
                :orientation="orientation"
                :value="focal"
                :min="min"
                :max="max"
                :step="step"
                :disabled="isMobile"
                :reversed="orientation === 'vertical'"
                @change="setFocal"
            />
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Utils from '../../../business/utils/Utils'

export default {
    name: 'FocalSelector',
    props: {
        orientation: {
            type: String,
            default: 'vertical'
        }
    },
    computed: {
        ...mapState({
            focusedWell: state => state.timelapse.focusedWell,
            playState: state => state.video.playState
        }),
        ...mapGetters({
            availableFocals: 'timelapse/availableFocals'
        }),
        isMobile() {
            return Utils.isMobileSize()
        },
        focal() {
            return this.focusedWell.focal
        },
        midFocalIndex() {
            return Math.round(this.availableFocals.length / 2) - 1
        },
        min() {
            return this.availableFocals[0]
        },
        step() {
            return this.availableFocals[1] - this.min
        },
        max() {
            return this.availableFocals[this.availableFocals.length - 1]
        }
    },
    methods: {
        setFocal(event) {
            this.$log.videoEvent('focal_changed', {
                fromFocal: this.focal,
                toFocal: event.newValue,
                playState: this.playState,
                method: 'slider'
            })

            this.$store.commit('timelapse/wellFocal', event.newValue)
        }
    }
}
</script>

<style lang="scss" scoped>
.focal-selector {
    display: flex;
    flex-direction: column;

    .slider-title {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        font-size: 9pt;
        color: $light-4;
        background-color: $dark-4;
        border-radius: $border-radius;

        .label,
        .value {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.25rem;
        }

        .label {
            border-right: 0.1rem solid $dark-2;
        }
    }

    .focal-selector-slider {
        position: relative;
        display: grid;
        width: 100%;

        .ticks-container {
            display: flex;
            justify-content: space-between;
            font-size: 9pt;
            color: $light;
        }
    }

    &.horizontal {
        .slider-title,
        .focal-selector-slider {
            height: 60px;
        }

        .slider-title {
            font-size: 12pt;

            .value {
                font-weight: 600;
            }
        }

        .focal-selector-slider {
            grid-template-rows: repeat(2, 1fr);

            .ticks-container {
                align-items: flex-end;
            }
        }

        @media screen and #{$new-break2close} {
            display: grid;
            column-gap: 20px;
            grid-template-columns: 1fr 2fr;
        }
    }

    &.vertical {
        .focal-selector-slider {
            grid-template-columns: repeat(3, 1fr);
            column-gap: 0.5rem;
            padding-top: 0.25rem;
            margin-top: 1rem;

            .ticks-container {
                flex-direction: column-reverse;
                text-align: right;
            }
        }
    }
}
</style>

<style lang="scss">
.focal-selector {
    .slider {
        &.slider-horizontal,
        &.slider-vertical {
            .slider-track,
            .slider-track-low,
            .slider-track-high,
            .slider-selection {
                background-image: none;
            }

            .slider-handle {
                background: $blue;
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 50%;
                cursor: pointer;
                border: 0.4rem solid $dark-4;
                box-shadow: $handle-shadow;
                z-index: 20;

                &:hover {
                    border-color: $dark-5;
                }
            }
        }

        &.slider-horizontal {
            width: 100%;

            .slider-track {
                height: 0.5rem;
                width: 100%;
                background-color: $dark-3;
            }
            .slider-handle {
                top: -0.2rem;
            }
        }

        &.slider-vertical {
            height: $video-left-control-height;

            .slider-track {
                width: 0.5rem !important;
                background-color: $dark-4;
            }
            .slider-handle {
                margin-left: -0.2rem;
            }
        }
    }
}
</style>
